import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '@/App';
import Header from '@/components/shared/Header';
import Footer from '@/components/shared/Footer';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { 
  Award, Eye, Calendar, BookOpen, CheckCircle, 
  FileText, Copy, Loader2, Shield, QrCode, ExternalLink
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StudentCertificates = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [certificates, setCertificates] = useState([]);

  useEffect(() => {
    if (!isAuthenticated) {
      toast.error('Please login to view certificates');
      navigate('/auth/login');
      return;
    }
    fetchCertificates();
  }, [isAuthenticated]);

  const fetchCertificates = async () => {
    try {
      // Fetch from multiple sources and merge
      const allCerts = [];
      
      // 1. Try premium certificates first
      try {
        const premiumRes = await axios.get(`${API}/certificate/my-certificates`);
        if (premiumRes.data?.length > 0) {
          allCerts.push(...premiumRes.data.map(c => ({
            ...c,
            source: 'premium'
          })));
        }
      } catch (e) {
        console.log('Premium certificates not available');
      }
      
      // 2. Also fetch old exam certificates
      try {
        const examCertsRes = await axios.get(`${API}/student/certificates`);
        if (examCertsRes.data?.length > 0) {
          // Transform to match premium format and avoid duplicates
          const examCerts = examCertsRes.data
            .filter(c => !allCerts.some(pc => pc.certificate_number === c.certificate_number))
            .map(c => ({
              id: c.id,
              certificate_number: c.certificate_number,
              student_name: c.student_name,
              course_name: c.course_name || c.exam_title,
              certificate_type: c.training_mode || 'STT',
              grade: getGradeFromPercentage(c.percentage),
              percentage: c.percentage,
              date_of_issue: c.issue_date,
              status: c.status || 'valid',
              source: 'exam'
            }));
          allCerts.push(...examCerts);
        }
      } catch (e) {
        console.log('Exam certificates not available');
      }
      
      // 3. Also check dynamic exam results for certificates
      try {
        const dynamicRes = await axios.get(`${API}/dynamic-exam/my-attempts`);
        const passedDynamic = dynamicRes.data?.filter(a => a.status === 'completed' && a.result === 'pass') || [];
        
        for (const attempt of passedDynamic) {
          // Check if already have this certificate
          if (!allCerts.some(c => c.course_name?.toLowerCase() === attempt.course_name?.toLowerCase())) {
            allCerts.push({
              id: `dynamic-${attempt.id}`,
              certificate_number: `IQRA/CAN/${attempt.id?.substring(0, 8)?.toUpperCase()}`,
              student_name: attempt.candidate_name,
              course_name: attempt.course_name || attempt.course_id,
              certificate_type: attempt.training_mode || 'STT',
              grade: getGradeFromPercentage(attempt.percentage),
              percentage: attempt.percentage,
              date_of_issue: attempt.end_time || attempt.created_at,
              status: 'valid',
              source: 'dynamic',
              attempt_id: attempt.id
            });
          }
        }
      } catch (e) {
        console.log('Dynamic exam results not available');
      }
      
      setCertificates(allCerts);
    } catch (error) {
      console.error('Failed to fetch certificates:', error);
    } finally {
      setLoading(false);
    }
  };
  
  // Helper function to calculate grade
  const getGradeFromPercentage = (percentage) => {
    if (!percentage) return 'N/A';
    if (percentage >= 90) return 'A+';
    if (percentage >= 80) return 'A';
    if (percentage >= 70) return 'B+';
    if (percentage >= 60) return 'B';
    if (percentage >= 50) return 'C';
    if (percentage >= 40) return 'D';
    return 'F';
  };

  const viewCertificate = (cert) => {
    // Navigate to certificate view page for all certificate types
    // The CertificateViewPage will handle fetching certificate details
    if (cert.source === 'premium' && cert.id) {
      navigate(`/certificate/${cert.id}`);
    } else if (cert.source === 'dynamic' && cert.attempt_id) {
      // For dynamic exams - navigate to certificate view with attempt_id
      navigate(`/certificate/dynamic/${cert.attempt_id}`);
    } else if (cert.id) {
      // For old exam certificates
      navigate(`/certificate/${cert.id}`);
    } else {
      // Fallback - use certificate number
      navigate(`/verify/${encodeURIComponent(cert.certificate_number)}`);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Certificate number copied!');
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <Header />
      
      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Hero Section */}
        <div className="bg-gradient-to-r from-yellow-500 via-amber-500 to-orange-500 rounded-2xl p-8 mb-8 text-white">
          <div className="flex items-center gap-4 mb-4">
            <div className="p-3 bg-white/20 rounded-xl backdrop-blur">
              <Award className="w-10 h-10" />
            </div>
            <div>
              <h1 className="text-3xl font-bold">My Certificates</h1>
              <p className="text-white/80">View and download your earned certificates</p>
            </div>
          </div>
          
          {/* Stats */}
          <div className="grid grid-cols-3 gap-4 mt-6">
            <div className="bg-white/10 backdrop-blur rounded-xl p-4 text-center">
              <p className="text-3xl font-bold">{certificates.length}</p>
              <p className="text-sm text-white/70">Total Certificates</p>
            </div>
            <div className="bg-white/10 backdrop-blur rounded-xl p-4 text-center">
              <p className="text-3xl font-bold">
                {certificates.filter(c => c.certificate_type === 'RPL').length}
              </p>
              <p className="text-sm text-white/70">RPL Certificates</p>
            </div>
            <div className="bg-white/10 backdrop-blur rounded-xl p-4 text-center">
              <p className="text-3xl font-bold">
                {certificates.filter(c => c.certificate_type === 'STT').length}
              </p>
              <p className="text-sm text-white/70">STT Certificates</p>
            </div>
          </div>
        </div>

        {/* Certificates List */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-10 h-10 animate-spin text-blue-600" />
          </div>
        ) : certificates.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
            <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-slate-700 mb-2">No Certificates Yet</h2>
            <p className="text-slate-500 mb-6">
              Complete your courses and pass the exams to earn certificates.
            </p>
            <Button onClick={() => navigate('/student/courses')}>
              <BookOpen className="w-4 h-4 mr-2" /> Browse Courses
            </Button>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {certificates.map((cert) => (
              <CertificateCard 
                key={cert.id} 
                certificate={cert} 
                onView={viewCertificate}
                onCopy={copyToClipboard}
                formatDate={formatDate}
              />
            ))}
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
};

// Certificate Card Component
const CertificateCard = ({ certificate, onView, onCopy, formatDate }) => {
  const isRPL = certificate.certificate_type === 'RPL';
  
  return (
    <div className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all group">
      {/* Header Banner */}
      <div className={`p-4 ${
        isRPL 
          ? 'bg-gradient-to-r from-purple-500 to-indigo-600' 
          : 'bg-gradient-to-r from-blue-500 to-cyan-500'
      }`}>
        <div className="flex items-center justify-between">
          <span className="inline-flex items-center gap-1 px-3 py-1 bg-white/20 backdrop-blur rounded-full text-white text-xs font-medium">
            <Award className="w-3 h-3" />
            {isRPL ? 'RPL Certificate' : 'STT Certificate'}
          </span>
          <Shield className="w-5 h-5 text-white/80" />
        </div>
        <h3 className="text-white font-bold mt-3 text-lg">
          {certificate.course_name}
        </h3>
      </div>

      {/* Content */}
      <div className="p-4 space-y-3">
        {/* Certificate Number */}
        <div className="flex items-center justify-between bg-slate-50 rounded-lg p-2">
          <div>
            <p className="text-xs text-slate-500">Certificate No.</p>
            <p className="font-mono text-sm font-medium text-slate-700">{certificate.certificate_number}</p>
          </div>
          <Button 
            variant="ghost" 
            size="sm"
            onClick={() => onCopy(certificate.certificate_number)}
          >
            <Copy className="w-4 h-4" />
          </Button>
        </div>

        {/* Details */}
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div>
            <p className="text-slate-500 text-xs">Grade</p>
            <p className={`font-bold ${
              certificate.grade === 'A+' ? 'text-green-600' :
              certificate.grade === 'A' ? 'text-blue-600' : 'text-slate-700'
            }`}>
              {certificate.grade} ({certificate.percentage?.toFixed(1)}%)
            </p>
          </div>
          <div>
            <p className="text-slate-500 text-xs">Issue Date</p>
            <p className="text-slate-700">{formatDate(certificate.date_of_issue)}</p>
          </div>
        </div>

        {/* Verification Status */}
        <div className="flex items-center gap-2 text-xs">
          <CheckCircle className="w-4 h-4 text-green-500" />
          <span className="text-green-600 font-medium">Verified & Active</span>
        </div>
      </div>

      {/* Actions */}
      <div className="border-t border-slate-100 p-3 flex gap-2">
        <Button 
          className="flex-1" 
          onClick={() => onView(certificate)}
        >
          <Eye className="w-4 h-4 mr-2" /> View & Download
        </Button>
        <Button 
          variant="outline"
          size="icon"
          onClick={() => window.open(`/verify/${encodeURIComponent(certificate.certificate_number)}`, '_blank')}
          title="Verify Certificate"
        >
          <QrCode className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};

export default StudentCertificates;
